<script setup>
import {computed, reactive, watchEffect, ref} from 'vue';
import {useAuthService} from '@/composables/useAuthService';
import {useMessaging} from '@/composables/useMessaging';
import SquashLevel from "@/components/players/SquashLevel.vue";
import AvatarImage from "@/components/images/AvatarImage.vue";
import {useValidation} from "@/composables/useValidation";
import {HIDDEN_CONTACTS_NOTE, UNBLOCK_TO_MESSAGE_LABEL, messageActionFor} from "@/composables/useMessagePermission.js";
import {useSafety} from "@/composables/useSafety";

const props = defineProps({
  id: String,
  player: {
    type: Object,
    default: () => null,
  },
  size: {
    type: String,
    default: 'small',
  },
  expandable: {
    type: Boolean,
    default: true,
  },
  // Off unless a competition surface asks for it. Default-true meant ratings
  // appeared on club-wide lists and non-squash contexts, since nothing here
  // knows the sport — only the caller does. See useSquashLevels.
  showSquashLevel: {
    type: Boolean,
    default: false,
  },
  inline: {
    type: Boolean,
    default: true,
  },
  minimized: {
    type: Boolean,
    default: false
  },
  highlighted: {
    type: Boolean,
    default: false
  },
  highlightColor : {
    type : String,
    default: 'info'
  },
  // Rendered handicap label ("+20", "Rec 15.3") passed down from TeamCard; null hides the chip.
  handicapLabel: {
    type: String,
    default: null,
  },
  // chip class from useHandicaps().chipClassFor — the one colour rule for handicap chips
  handicapTone: {type: String, default: 'tl-chip--brand'},
  maxLines: {
    type: Number,
    default: null,
  },
  // Label shown on the row instead of the player's own name — a singles team an
  // admin renamed (TeamCard passes the override). The dialog still names the player.
  displayName: {
    type: String,
    default: null,
  },
  // Sparse-row mode: render from the (partial) `player` object, but fetch the
  // full player record only when the dialog is opened — so a standings table
  // of 12 rows costs zero extra requests until someone taps a player.
  fetchOnExpand: {
    type: Boolean,
    default: false,
  },
});
const { isGuid } = useValidation();
const {getItem, itemPending : loading, itemErrors : errors, item, hasItem} = useCrudApi('api/v2/players');
const {user, impersonateUser, impersonateLoading, impersonateErrors, isSuperUser, isAuthenticated} = useAuthService();
const { createConversation } = useMessaging();
const { blockedUserIds, loadBlocked, unblock } = useSafety();
const router = useRouter();
const route = useRoute();
const messageLoading = ref(false);

watchEffect(() => {
  if (props.id && !props.fetchOnExpand) {
    getItem(props.id);
  }
});
// fetchOnExpand: once the full record arrives it wins over the sparse row.
const currentItem = computed(() =>
    (props.fetchOnExpand && hasItem.value ? item.value : null) || props.player || item.value);
const state = reactive({
  dialog: false,
});
const handleClick = () => {
  if (!props.expandable) return;
  if (props.fetchOnExpand && !hasItem.value) {
    const playerId = props.id || props.player?.id || props.player?.playerId;
    if (playerId) getItem(playerId);
  }
  state.dialog = true;
  // Who the viewer has blocked decides between Message and Unblock to
  // message. Loaded once per session (cached), only when a card is opened.
  if (isAuthenticated.value) loadBlocked();
};

const style = computed(() => {
  return {
    cursor: props.expandable ? 'pointer' : 'default',
  };
});
const nameStyle = computed(() => {
  return {
    fontSize: props.size === 'small' ? '14px' : (props.size === 'medium' ? '18px' : '24px'),
    fontWeight: 600,
  };
});
// Avatar should sit just slightly taller than the name text (≈1.3× the font
// size), not dominate it: small 14px→20, medium 18px→24, large 24px→32.
const avatarSize = computed(() => {
  return props.size === 'small' ? 20 : (props.size === 'medium' ? 24 : 32);
});
const avatarClasses = computed(() => {
  return {
    'mr-1': !props.minimized && props.size === 'small',
    'mr-2': !props.minimized && props.size !== 'small'
  }
});
const nameClampClass = computed(() => {
  if (!props.maxLines) return null;
  if (props.maxLines === 1) return 'text-truncate';
  return `line-clamp-${props.maxLines}`;
});

// ── Player dialog (mirrors the Flutter PlayerDialog bottom sheet) ──
const isMe = computed(() =>
    isAuthenticated.value && isGuid(currentItem.value?.userId) && currentItem.value?.userId === user.value?.id);
// Never read off a hidden player's stub: it carries no account either way.
const adminManaged = computed(() =>
    !currentItem.value?.obfuscated && !isGuid(currentItem.value?.userId) && !currentItem.value?.hasInvite);
const playerHasInvite = computed(() => !!currentItem.value?.hasInvite);
const hasEmail = computed(() => !!(currentItem.value?.email && currentItem.value.email.length > 1));
const hasPhone = computed(() => !!(currentItem.value?.telephone && currentItem.value.telephone.length > 1));
// Whether the viewer may start a chat: the API's canMessage, NOT "not
// obfuscated", which only hides email and phone (release fixes, 28 Sep 2026).
// Someone the viewer blocked gets "Unblock to message" instead (29 Sep 2026).
const messageAction = computed(() =>
    messageActionFor(currentItem.value, {
      signedIn: isAuthenticated.value, myUserId: user.value?.id, blockedUserIds: blockedUserIds.value,
    }));

// Unblock, then start the chat as Message would. A refusal (they are no longer
// in a club you share, say) is shown by createConversation and nothing opens.
const handleUnblockToMessage = async () => {
  if (!currentItem.value?.userId) return;
  try {
    messageLoading.value = true;
    await unblock(currentItem.value.userId);
  } catch (error) {
    messageLoading.value = false;
    return;
  }
  await handleMessage();
};

// Follow (PRIVACY_PLAN.md §3.6): only for someone the viewer shares no club with,
// and only between adults; the API's followStatus says which, per viewer.
const {request: requestFollow, accept: acceptFollow, remove: removeFollow, list: listFollows, reasonOf: followReason} = useFollows();
const followBusy = ref(false);
const followStatus = computed(() => currentItem.value?.followStatus ?? null);
const showFollow = computed(() => ['none', 'requested', 'requestReceived', 'following'].includes(followStatus.value));
const followLabel = computed(() => FOLLOW_LABELS[followStatus.value] ?? 'Follow');
const handleFollow = async () => {
  if (followBusy.value || !currentItem.value) return;
  followBusy.value = true;
  try {
    if (followStatus.value === 'requestReceived') {
      const mine = await listFollows();
      const received = (mine || []).find(f => f.direction === 'received' && f.status === 0 && f.person?.playerId === currentItem.value.id);
      if (received) await acceptFollow(received.id);
      currentItem.value.followStatus = 'following';
    } else if (followStatus.value === 'none') {
      await requestFollow(currentItem.value.id);
      currentItem.value.followStatus = 'requested';
    } else if (followStatus.value === 'requested') {
      const mine = await listFollows();
      const sent = (mine || []).find(f => f.direction === 'sent' && f.status === 0 && f.person?.playerId === currentItem.value.id);
      if (sent) await removeFollow(sent.id);
      currentItem.value.followStatus = 'none';
    }
  } catch (e) {
    showError(followReason(e));
  } finally {
    followBusy.value = false;
  }
};

const handleMessage = async () => {
  if (!currentItem.value?.userId) return;

  try {
    messageLoading.value = true;
    const conversation = await createConversation([currentItem.value.userId], 1, null);
    router.push({
      query: { ...route.query, conversationId: conversation.id, messaging: 'open' }
    });
    state.dialog = false;
  } catch (error) {
    console.error('Failed to create conversation:', error);
  } finally {
    messageLoading.value = false;
  }
};

</script>

<template>
  <div :style="style" v-if="currentItem" @click="handleClick" :class="inline ? 'flex-wrap d-flex' :''">
    <div class="d-flex align-center">
      <v-badge icon="mdi-arrow-bottom-left" :model-value="highlighted" :color="highlightColor" :floating="true">
        <v-tooltip :disabled="!minimized" :text="displayName || currentItem.fullName" location="bottom">
          <template v-slot:activator="{ props }">
            <avatar-image v-bind="props" :size="avatarSize" :class="avatarClasses" :filename="currentItem.imageFileName"
                          :fallback="currentItem.initials"/>
            <!-- flex-wrap: the handicap chip drops under the name when the column is tight (phones, matrices) -->
            <div v-if="!minimized" class="align-center d-flex flex-wrap name-row" :style="nameStyle">
              <span :class="nameClampClass">{{ displayName || currentItem.fullName }}</span>
              <span v-if="handicapLabel && !minimized" class="tl-chip pc-hcp" :class="handicapTone">{{ handicapLabel }}</span>
              <div class="ml-2"
                   v-if="(size === 'medium' || size === 'large') && showSquashLevel && currentItem.squashLevel > 0">
                <squash-level size="x-small"
                              :squash-level-id="currentItem.squashLevelId"
                              :level="currentItem.squashLevel"/>
              </div>
            </div>
          </template>
        </v-tooltip>
      </v-badge>
    </div>
    <squash-level class="ml-2" size="x-small" v-if="size === 'small' && showSquashLevel && currentItem.squashLevel > 0"
                  :squash-level-id="currentItem.squashLevelId"
                  :level="currentItem.squashLevel"/>
  </div>
  <div v-else-if="loading">
    Loading...
  </div>
  <div v-else-if="errors.length">
    <crud-error-display :errors="errors"/>
  </div>
  <v-dialog v-model="state.dialog" max-width="440">
    <div class="tl-sheet" v-if="currentItem">
      <!-- head: close only -->
      <div class="tl-sheet-head">
        <v-btn :icon="true" variant="text" size="small" @click="state.dialog = false">
          <v-icon icon="mdi-close"/>
        </v-btn>
      </div>

      <!-- centred identity -->
      <div class="tl-sheet-id">
        <avatar-image :size="76" :filename="currentItem.imageFileName" :fallback="currentItem.initials"/>
        <div class="tl-sheet-name">{{ currentItem.fullName || 'Member' }}</div>
        <div v-if="currentItem.squashLevel > 0" class="mt-1">
          <squash-level size="x-small" :squash-level-id="currentItem.squashLevelId" :level="currentItem.squashLevel"/>
        </div>
        <div v-if="adminManaged || playerHasInvite || isMe" class="d-flex justify-center ga-2 mt-3">
          <span v-if="playerHasInvite" class="tl-chip tl-chip--ok">Awaiting invite acceptance</span>
          <span v-else-if="adminManaged" class="tl-chip tl-chip--warn">Admin managed</span>
          <span v-if="isMe" class="tl-chip tl-chip--brand">Your account</span>
        </div>
      </div>

      <!-- contact card -->
      <div v-if="hasEmail || hasPhone" class="tl-sheet-body">
        <div class="tl-contact-card">
          <a v-if="hasEmail" class="tl-contact-row" :href="currentItem.obfuscated ? undefined : 'mailto:' + currentItem.email">
            <div class="tl-contact-tile"><v-icon size="21">mdi-email-outline</v-icon></div>
            <div class="tl-contact-text">
              <div class="tl-contact-value" :class="{ 'tl-contact-value--locked': currentItem.obfuscated }">{{ currentItem.email }}</div>
              <div v-if="!currentItem.obfuscated" class="tl-contact-hint">Tap to email</div>
            </div>
            <v-icon v-if="currentItem.obfuscated" size="18" color="textSubtle">mdi-lock-outline</v-icon>
          </a>
          <a v-if="hasPhone" class="tl-contact-row" :href="currentItem.obfuscated ? undefined : 'tel:' + currentItem.telephone">
            <div class="tl-contact-tile"><v-icon size="21">mdi-phone-outline</v-icon></div>
            <div class="tl-contact-text">
              <div class="tl-contact-value" :class="{ 'tl-contact-value--locked': currentItem.obfuscated }">{{ currentItem.telephone }}</div>
              <div v-if="!currentItem.obfuscated" class="tl-contact-hint">Tap to call</div>
            </div>
            <v-icon v-if="currentItem.obfuscated" size="18" color="textSubtle">mdi-lock-outline</v-icon>
          </a>
        </div>
        <div v-if="currentItem.obfuscated" class="tl-sheet-note mt-2">
          {{ HIDDEN_CONTACTS_NOTE }}
        </div>
      </div>

      <!-- foot: Stats | Message -->
      <div class="tl-sheet-foot">
        <v-btn
            variant="tonal"
            color="primary"
            size="large"
            class="flex-grow-1"
            prepend-icon="mdi-chart-line"
            :to="{ name: 'PlayerStats', params: { playerId: currentItem.id } }"
        >Stats</v-btn>
        <v-btn
            v-if="messageAction === 'message'"
            color="primary"
            size="large"
            class="flex-grow-1"
            prepend-icon="mdi-chat-outline"
            :loading="messageLoading"
            @click="handleMessage"
        >Message</v-btn>
        <v-btn
            v-else-if="messageAction === 'unblock'"
            color="primary"
            variant="tonal"
            size="large"
            class="flex-grow-1"
            prepend-icon="mdi-account-lock-open-outline"
            :loading="messageLoading"
            @click="handleUnblockToMessage"
        >{{ UNBLOCK_TO_MESSAGE_LABEL }}</v-btn>
        <v-btn
            v-else-if="showFollow && followStatus !== 'following'"
            color="primary"
            :variant="followStatus === 'requested' ? 'tonal' : 'flat'"
            size="large"
            class="flex-grow-1"
            prepend-icon="mdi-account-heart-outline"
            :loading="followBusy"
            @click="handleFollow"
        >{{ followLabel }}</v-btn>
      </div>
      <div v-if="showFollow && followStatus === 'none'" class="tl-sheet-note px-5 pb-4">
        Follow to see their full name and profile, and to message them. They will need to accept.
      </div>
      <div v-else-if="followStatus === 'requested'" class="tl-sheet-note px-5 pb-4">
        Waiting for them to accept. Tap Requested to cancel.
      </div>

      <!-- super admin: impersonate -->
      <div v-if="isSuperUser && !isMe && isGuid(currentItem.userId)" class="px-5 pb-5">
        <v-btn variant="text" block :loading="impersonateLoading" prepend-icon="mdi-login"
               @click="impersonateUser(currentItem.userId)">Impersonate user</v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<style scoped>
/* Line clamp styles for multi-line text truncation with ellipsis */
.line-clamp-2,
.line-clamp-3,
.line-clamp-4,
.line-clamp-5 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.line-clamp-2 { -webkit-line-clamp: 2; }
.line-clamp-3 { -webkit-line-clamp: 3; }
.line-clamp-4 { -webkit-line-clamp: 4; }
.line-clamp-5 { -webkit-line-clamp: 5; }
.pc-hcp {
  font-size: 10px;
  font-weight: 700;
  padding: 0 6px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* the name row: an 8px gap beside the name while the chip fits on the line, a 4px gap above it
   once the column is too tight and it wraps — the gap follows the wrap, not the screen width */
.name-row {
  column-gap: 8px;
  row-gap: 4px;
}
</style>
